import React, { useState } from 'react';

import { useMutation } from '@apollo/client/react';
import { c, msgid, ngettext } from 'ttag';

import { Alert, Button, Modal, ModalContent, ModalFooter, Textarea } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ArchiveDashboardDocument, AssignableDashboardsDocument } from '../../graphql/__generated__/types';
import { catalogueRefusalOf } from './refusals';

interface Props {
    entry: { id: string; name: string };
    accountName: string;
    onClose: () => void;
}

/**
 * Withdraw an entry from an account's catalogue.
 *
 * **Attention, archiving is not deleting, and the confirmation has to say what survives.** A
 * `DELETE` would cascade through `dashboard_membership` and unplug the holders in
 * silence, erasing the answer to « who had it » six months later. So the row stays,
 * the habilitations stay with it, and what changes is that the entry leaves the
 * catalogue: it disappears from the onboarding picker and from `inviteUser`.
 *
 * **Attention, which means the operator must not read « archiver » as « couper l'accès ».**
 * The content closes only once the token maker reads `archived_at`, and that is
 * PCG-26: it does not exist yet. Saying « c'est coupé » here would be the one
 * sentence this dialog cannot afford.
 *
 * **`holderCount` is shown after the gesture and not before, because that is the only
 * moment it is known.** No query returns it: the mutation's payload is its single
 * source. Unlike `closeAccountToPortal`, which refuses while people are attached,
 * this mutation does not refuse: archiving genuinely closes, so what is owed to the
 * operator is the number, not a veto.
 */
const ArchiveDashboardDialog = ({ entry, accountName, onClose }: Props) => {
    const [reason, setReason] = useState('');

    const [archive, { loading, error, data }] = useMutation(ArchiveDashboardDocument, {
        refetchQueries: [AssignableDashboardsDocument],
        awaitRefetchQueries: true,
        // Same eviction as the declaration: the onboarding journey reads this catalogue
        // under its own variables, and a cached entry would keep offering an archived
        // dashboard that `inviteUser` now refuses.
        update: (cache) => {
            cache.evict({ id: 'ROOT_QUERY', fieldName: 'assignableDashboards' });
            cache.gc();
        },
    });

    const done = data?.archiveDashboard;
    const holders = done?.holderCount ?? 0;

    const submit = () => {
        archive({ variables: { dashboardId: entry.id, reason: reason.trim() } }).catch(() => {});
    };

    // Required on screen although the API takes it as optional: it is journalled, and
    // it is the only trace of why the entry left the catalogue.
    const blocked = reason.trim().length === 0;

    return (
        <Modal open onClose={onClose} title={c('Dashboard catalogue').t`Archive this dashboard`} size={580}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-900">
                        <b>{entry.name}</b>
                        <span className="text-text-600"> · {accountName}</span>
                    </p>

                    {done ? (
                        <Alert type="success" title={c('Dashboard catalogue').t`The entry has left the catalogue.`}>
                            <p className="text-sm">
                                {/* The number, said plainly. It is the answer to « what did I
                                    just do », and it cannot be looked up afterwards. */}
                                {holders === 0
                                    ? c('Dashboard catalogue')
                                          .t`Nobody held it, so this archiving unplugged no one. It can no longer be granted, and the same Metabase dashboard may be declared again on this account whenever needed.`
                                    : ngettext(
                                          msgid`${holders} person still holds it, and keeps holding it: archiving withdraws the entry from the catalogue, it does not revoke what was granted. That is deliberate — it is what answers « who had it » later on.`,
                                          `${holders} people still hold it, and keep holding it: archiving withdraws the entry from the catalogue, it does not revoke what was granted. That is deliberate — it is what answers « who had it » later on.`,
                                          holders
                                      )}
                            </p>
                            {holders > 0 && (
                                <p className="mt-2 text-sm">
                                    {c('Dashboard catalogue')
                                        .t`Their access closes for good once the portal reads this archiving when it signs a dashboard, which is a separate piece of work still to come. Until then, treat those people as still able to open it.`}
                                </p>
                            )}
                        </Alert>
                    ) : (
                        <>
                            <Alert type="warning">
                                {c('Dashboard catalogue')
                                    .t`Archiving withdraws the entry from the catalogue: it can no longer be granted to anybody. It does not revoke the people who already hold it — their habilitations survive on purpose, so that who had access stays answerable. How many people that is will be shown once it is done; it cannot be counted beforehand.`}
                            </Alert>

                            <div>
                                <label
                                    htmlFor="archive-dashboard-reason"
                                    className="mb-1.5 block text-sm font-medium text-text-900"
                                >
                                    {c('Dashboard catalogue').t`Reason for archiving`}
                                </label>
                                <Textarea
                                    id="archive-dashboard-reason"
                                    value={reason}
                                    onValue={setReason}
                                    placeholder={c('Dashboard catalogue')
                                        .t`Dashboard replaced, reporting discontinued…`}
                                />
                                <p className="mt-1.5 text-xs text-text-600">
                                    {c('Dashboard catalogue')
                                        .t`It is journalled with the gesture, and it is the only trace of why this entry was withdrawn.`}
                                </p>
                            </div>

                            {error && <RefusalNotice refusal={catalogueRefusalOf(error)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                {done ? (
                    <Button category="default" onClick={onClose}>
                        {c('Action').t`Done`}
                    </Button>
                ) : (
                    <>
                        <Button category="weak" onClick={onClose}>
                            {c('Action').t`Cancel`}
                        </Button>
                        <Button category="destructive" loading={loading} disabled={blocked} onClick={submit}>
                            {c('Dashboard catalogue').t`Archive the entry`}
                        </Button>
                    </>
                )}
            </ModalFooter>
        </Modal>
    );
};

export default ArchiveDashboardDialog;
